import React, { useMemo, useState } from 'react';
import {
  Box,
  CloseButton,
  Flex,
  BoxProps,
  Text,
  Image,
} from '@chakra-ui/react';
import { SkipNavLink } from '@chakra-ui/skip-nav';
import { useAppSelector } from '@spartalabs/pdc-core';
import isotipoSvg from 'assets/svg/isotipo.svg';
import logoSvg from 'assets/svg/logo.svg';
import { isProd } from 'utils/functions';
import {
  hasAnyAdminPermission,
  hasAnyPortalPermission,
  hasAnySchedulePermission,
  hasAnyVehicleManagementPermission,
  hasPortalPermission,
} from 'utils/hasPortalPermission';
import { NavBtn } from '../NavButtons';

interface SidebarProps extends BoxProps {
  onClose: () => void;
  isCollapsed: boolean;
  sideBarName: string;
}

interface optionProps {
  fp: string[];
  sp: string[];
  tp: string[];
}

type DropItem = {
  name: string;
  path: string;
  insideDrops?: DropItem[];
};

export const SidebarContent = ({
  onClose,
  isCollapsed,
  sideBarName,
  ...rest
}: SidebarProps) => {
  const permissions = useAppSelector(
    state => state.authSlicer.user.permissions,
  );

  const [option, setOption] = useState<optionProps>({
    fp: ['home'],
    sp: [],
    tp: [],
  });

  const reportDrop = useMemo(() => {
    const drop: DropItem[] = [];

    if (hasPortalPermission(permissions, 'models')) {
      drop.push({
        name: 'Modelos',
        path: '/relatorios/modelos',
      });
    }

    if (hasPortalPermission(permissions, 'fleetDist')) {
      drop.push({
        name: 'Distribuição da Frota',
        path: '/relatorios/distdefrotas',
      });
    }

    /* Menu desativado — Disponibilidade não deve aparecer no portal.
    drop.push({
      name: 'Disponibilidade',
      path: '/relatorios/disponibilidade',
    });
    */

    const maintenanceItems: DropItem[] = [];

    if (hasPortalPermission(permissions, 'entraceExit')) {
      maintenanceItems.push({
        name: 'Entrada e Saída',
        path: '/relatorios/manutencao/entradasaida',
      });
    }

    /* Menu desativado — Preventivas não deve aparecer no portal.
    maintenanceItems.push({
      name: 'Preventivas',
      path: '/relatorios/manutencao/preventivas',
    });
    */

    if (hasPortalPermission(permissions, 'services')) {
      maintenanceItems.push({
        name: 'Serviços',
        path: '/relatorios/manutencao/servicos',
      });
    }

    if (maintenanceItems.length > 0) {
      drop.push({
        name: 'Manutenção',
        path: '/manutencao',
        insideDrops: maintenanceItems,
      });
    }

    return drop;
  }, [permissions]);

  const vehiclesDrop = useMemo(() => {
    const drop: DropItem[] = [];

    if (hasPortalPermission(permissions, 'crlv')) {
      drop.push({
        name: 'Veículos/CRLV',
        path: 'gestao/veiculos',
      });
    }

    if (hasPortalPermission(permissions, 'damageCharge')) {
      drop.push({
        name: 'Cobrança de Avarias',
        path: 'gestao/cobrancas',
      });
    }

    return drop;
  }, [permissions]);

  const AdmDrop = useMemo(() => {
    const drop: DropItem[] = [];

    if (hasPortalPermission(permissions, 'communique')) {
      drop.push({
        name: 'Comunicados',
        path: 'administrativo/comunicados',
      });
    }

    if (hasPortalPermission(permissions, 'logs')) {
      drop.push({
        name: 'Log de ações',
        path: 'administrativo/logs',
      });

      drop.push({
        name: 'Logs Pdv',
        path: 'administrativo/logspdv',
      });
    }

    if (!isProd() && hasPortalPermission(permissions, 'access')) {
      drop.push({
        name: 'Acessos',
        path: '/acessos',
      });
    }

    return drop;
  }, [permissions]);

  const showSchedules = hasAnySchedulePermission(permissions);
  const showVehicleManagement = hasAnyVehicleManagementPermission(permissions);
  const showAdministrative = hasAnyAdminPermission(permissions);

  return (
    <Box
      zIndex={10}
      bg="mutable.100"
      w={{ base: 'full', xl: isCollapsed ? '86px' : '248px' }}
      pos="fixed"
      h="full"
      px={isCollapsed ? '16px' : '12px'}
      pt="44px"
      transition=" width ease-in-out 0.3s"
      overflowY={isCollapsed ? 'visible' : 'scroll'}
      sx={{
        '::-webkit-scrollbar': {
          display: 'none',
        },
      }}
      {...rest}
    >
      <Flex justifyContent="space-between" mb="34px" alignItems="center">
        <Image
          alt=""
          pl="8px"
          src={isCollapsed ? isotipoSvg : logoSvg}
          w="100px"
        />
        <CloseButton
          data-cy={`close-${sideBarName}`}
          display={{ base: 'flex', xl: 'none' }}
          onClick={onClose}
        />
      </Flex>
      <SkipNavLink>
        <Text textColor="text.100">Pular navegação</Text>
      </SkipNavLink>

      {hasPortalPermission(permissions, 'home') && (
        <NavBtn
          onClose={onClose}
          isCollapsed={isCollapsed}
          option={option}
          setOption={setOption}
          name="Início"
          path="/inicio"
        />
      )}
      {reportDrop.length > 0 && (
        <NavBtn
          onClose={onClose}
          isCollapsed={isCollapsed}
          option={option}
          setOption={setOption}
          name="Relatórios"
          isDropable
          dropContent={reportDrop}
          path="/relatorios"
        />
      )}
      {showSchedules && (
        <NavBtn
          onClose={onClose}
          isCollapsed={isCollapsed}
          option={option}
          setOption={setOption}
          name="Agendamentos"
          path="/agendamentos"
        />
      )}

      {hasPortalPermission(permissions, 'callCenter') && (
        <NavBtn
          onClose={onClose}
          isCollapsed={isCollapsed}
          option={option}
          setOption={setOption}
          name="Central de Chamados"
          path="/chamados"
        />
      )}

      {hasPortalPermission(permissions, 'maintenanceServices') && (
        <NavBtn
          onClose={onClose}
          isCollapsed={isCollapsed}
          option={option}
          setOption={setOption}
          name="Serviços de Manutenções"
          path="/servicos-manutencao"
        />
      )}

      {hasAnyPortalPermission(permissions) && (
        <NavBtn
          onClose={onClose}
          isCollapsed={isCollapsed}
          option={option}
          setOption={setOption}
          name="Multas"
          path="/portal/multas"
        />
      )}

      {hasPortalPermission(permissions, 'finan') && (
        <NavBtn
          onClose={onClose}
          isCollapsed={isCollapsed}
          option={option}
          setOption={setOption}
          name="Faturamento"
          path="/faturamento"
        />
      )}

      {/* Menu desativado — Solicitações não deve aparecer no portal.
      <NavBtn
        onClose={onClose}
        isCollapsed={isCollapsed}
        option={option}
        setOption={setOption}
        name="Solicitações"
        path="/solicitacoes"
      />
      */}

      {hasPortalPermission(permissions, 'vamosControl') && (
        <NavBtn
          onClose={onClose}
          isCollapsed={isCollapsed}
          option={option}
          setOption={setOption}
          name="Vamos Controle"
          path="https://www.vamoscontrole.com.br/iweb2/loginvamoscontrole.aspx"
        />
      )}

      {hasPortalPermission(permissions, 'faq') && (
        <NavBtn
          onClose={onClose}
          isCollapsed={isCollapsed}
          option={option}
          setOption={setOption}
          name="Central de Dúvidas"
          path="/centraldeduvidas"
        />
      )}
      {/* Menu desativado — Gestão de contratos não deve aparecer no portal.
      {!isProd() && (
        <NavBtn
          onClose={onClose}
          isCollapsed={isCollapsed}
          option={option}
          setOption={setOption}
          name="Gestão de contratos"
          path="/gestaodecontratos"
        />
      )}
      */}

      {showVehicleManagement && vehiclesDrop.length > 0 && (
        <NavBtn
          onClose={onClose}
          isCollapsed={isCollapsed}
          option={option}
          setOption={setOption}
          isDropable
          dropContent={vehiclesDrop}
          name="Gestão de Veículos"
          path="/gestao"
        />
      )}
      {showAdministrative && AdmDrop.length > 0 && (
        <NavBtn
          onClose={onClose}
          isCollapsed={isCollapsed}
          option={option}
          setOption={setOption}
          name="Administrativo"
          isDropable
          dropContent={AdmDrop}
          path="/administrativo"
        />
      )}
    </Box>
  );
};
